import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronLeft } from "lucide-react";
import { useSeo, JsonLd, SITE_URL } from "@/lib/seo";
import { Footer } from "@/components/landing/Footer";
import { ExchangeMark, DATA_PAGE } from "@/components/home/ExchangeLogos";
import { FEEDS, EXCHANGES_DATA, FAQ } from "@/data/exchangeData";

const ALPHA = "https://alpha.tradrquant.com/terminal";
const LBL = "font-mono text-[11px] uppercase tracking-[0.22em] text-[#7D8390]";

const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};
const crumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "Exchange data", item: `${SITE_URL}${DATA_PAGE}` },
  ],
};

const Header = () => (
  <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
    <div className="mx-auto flex h-14 max-w-[1200px] items-center justify-between px-5 md:px-10">
      <Link to="/" className="group flex items-center gap-2.5">
        <img src="/tq-logo.png?v=3" alt="TradrQuant" className="h-[22px] w-auto transition-transform duration-300 group-hover:scale-105" />
        <span className="tq-wordmark text-[19px] text-[#ECEBE4] sm:text-[21px]">Tradr<span className="tq-q">Q</span>uant</span>
      </Link>
      <Link to="/" data-testid="data-back-btn" className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:border-white/40 hover:text-white">
        <ChevronLeft size={14} /> cd ~/home
      </Link>
    </div>
  </header>
);

const Cta = ({ tid }) => (
  <a href={ALPHA} data-testid={tid} className="group inline-flex items-center gap-2 bg-[#BACD1C] px-6 py-3.5 font-mono text-[12px] uppercase tracking-[0.14em] text-black transition-colors duration-300 hover:bg-[#ECEBE4]">
    Open free terminal <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
  </a>
);

const FeedGlossary = () => (
  <section className="border-t border-white/[0.07] py-16" data-testid="data-feeds-section">
    <h2 className={LBL}>The feeds, in plain English</h2>
    <div className="mt-8 grid gap-px bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-3">
      {Object.entries(FEEDS).map(([id, f]) => (
        <article key={id} data-testid={`feed-${id}`} className="bg-[#0A0A0A] p-6">
          <p className="font-mono text-[11px] text-[#FFB224]">{f.k}</p>
          <h3 className="mt-2 font-display text-lg font-medium text-[#ECEBE4]">{f.t}</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#9DA3AE]">{f.what}</p>
          <p className="mt-3 text-sm leading-relaxed text-[#C9CCD1]"><span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#7D8390]">How traders use it · </span>{f.use}</p>
        </article>
      ))}
    </div>
  </section>
);

const ExchangeSection = ({ x }) => (
  <section id={x.id} data-testid={`exchange-section-${x.id}`} className="scroll-mt-20 border-t border-white/[0.07] py-14 md:grid md:grid-cols-[260px_1fr] md:gap-12">
    <div>
      <div className="flex items-center gap-3 text-[#ECEBE4]">
        <ExchangeMark id={x.id} className={x.id === "bybit" ? "h-5" : "h-6"} />
        {x.id !== "bybit" && <span className="font-display text-xl font-medium">{x.name}</span>}
      </div>
      <p className={`mt-3 ${LBL}`}>{x.kind}</p>
    </div>
    <div className="mt-6 md:mt-0">
      <h2 className="font-display text-2xl font-medium tracking-[-0.02em] text-[#ECEBE4] md:text-3xl">{x.name} order flow &amp; real-time data</h2>
      <p className="mt-4 max-w-3xl leading-relaxed text-[#9DA3AE]">{x.intro}</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {x.feeds.map((f) => (
          <span key={f} className="border border-white/[0.10] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-[#C9CCD1]">
            <span className="text-[#FFB224]">{FEEDS[f].k}</span> {FEEDS[f].t}
          </span>
        ))}
      </div>
      <p className="mt-6 max-w-3xl border-l border-[#BACD1C]/40 pl-4 text-sm leading-relaxed text-[#C9CCD1]">{x.edge}</p>
    </div>
  </section>
);

const Faq = () => (
  <section className="border-t border-white/[0.07] py-16" data-testid="data-faq-section">
    <h2 className={LBL}>FAQ</h2>
    <div className="mt-8 divide-y divide-white/[0.07]">
      {FAQ.map((f, i) => (
        <details key={f.q} data-testid={`data-faq-${i}`} className="group py-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-6 font-display text-lg text-[#ECEBE4]">
            {f.q}<span className="font-mono text-[#7D8390] transition-transform group-open:rotate-45">+</span>
          </summary>
          <p className="mt-3 max-w-3xl leading-relaxed text-[#9DA3AE]">{f.a}</p>
        </details>
      ))}
    </div>
  </section>
);

export default function ExchangeData() {
  const { hash } = useLocation();
  useSeo({
    title: "Hyperliquid, Binance & OKX Order Flow Data — Real-Time, Low Latency | TradrQuant",
    description: "Real-time, low-latency crypto order flow from Hyperliquid, Binance, OKX, Bybit and Coinbase: trades, order book depth, open interest, funding, liquidations and Hyperliquid whale wallets in one terminal.",
    path: DATA_PAGE,
  });
  useEffect(() => {
    const el = hash && document.getElementById(hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    else window.scrollTo(0, 0);
  }, [hash]);

  return (
    <div data-testid="exchange-data-page" className="grain relative min-h-screen overflow-x-hidden bg-[#0A0A0A] text-white">
      <JsonLd data={[faqLd, crumbLd]} />
      <Header />
      <main className="mx-auto max-w-[1200px] px-5 md:px-10">
        <section className="py-16 md:py-24" data-testid="data-hero">
          <p className={LBL}>Data · multi-exchange · low latency</p>
          <h1 className="mt-5 max-w-4xl font-display text-[2.1rem] font-semibold leading-[1.05] tracking-[-0.035em] text-[#ECEBE4] sm:text-5xl lg:text-[3.5rem]">
            Hyperliquid, Binance &amp; OKX order flow. <span className="text-[#7D8390]">Real time, one terminal.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#9DA3AE] md:text-lg">
            TradrQuant streams live market data from Hyperliquid, Binance, OKX, Bybit and Coinbase with low latency: trades, order book depth,
            open interest, funding, liquidations and tracked Hyperliquid whale wallets. Here is what each feed is and how traders use it.
          </p>
          <nav className="mt-8 flex flex-wrap gap-x-6 gap-y-3" data-testid="data-exchange-nav">
            {EXCHANGES_DATA.map((x) => (
              <a key={x.id} href={`#${x.id}`} data-testid={`data-nav-${x.id}`} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#7D8390] transition-colors duration-300 hover:text-[#ECEBE4]">
                <ExchangeMark id={x.id} className={x.id === "bybit" ? "h-3" : "h-3.5"} />{x.id !== "bybit" && x.name}
              </a>
            ))}
          </nav>
          <div className="mt-10"><Cta tid="data-hero-cta" /></div>
        </section>
        <FeedGlossary />
        {EXCHANGES_DATA.map((x) => <ExchangeSection key={x.id} x={x} />)}
        <Faq />
        <section className="flex flex-col items-start gap-6 border-t border-white/[0.07] py-16 md:flex-row md:items-center md:justify-between">
          <p className="max-w-xl font-display text-2xl text-[#ECEBE4]">See the live BTC and ETH order flow for free.</p>
          <Cta tid="data-bottom-cta" />
        </section>
      </main>
      <Footer />
    </div>
  );
}
